<template>
  <div>
    <!-- Help content dialog -->
    <v-dialog
      :value="showDialog"
      @input="closeDialog"
      max-width="800px"
      scrollable
    >
      <v-card>
        <v-card-title class="headline pa-4">
          <v-btn 
            v-if="showBackButton" 
            icon 
            @click="goBack"
            class="back-button"
            style="margin-left: -12px; margin-right: 8px;"
          >
            <v-icon color="#91268F">fa-arrow-left</v-icon>
          </v-btn>
          {{ breadcrumbPath }}
          <v-spacer></v-spacer>
          <BaseIcon
            icon="close"
            @click="closeDialog"
            :alvqa="'context-help-dialog-close'"
          />
        </v-card-title>
        
        <v-card-text v-if="isLoading" class="text-center pa-4">
          <v-progress-circular indeterminate></v-progress-circular>
          <div class="mt-2">{{ $t('lang_GENERIC_CAPTION_LOADING') }}</div>
        </v-card-text>
        
        <v-card-text v-else-if="helpSections && helpSections.length" class="pa-4">
          <!-- Main content section -->
          <div v-if="helpSections[0]">
            <h3 v-if="helpSections[0].headline" class="mb-4">
              {{ helpSections[0].headline }}
            </h3>
            
            <!-- Main section image - 100% width -->
            <v-img 
              v-if="helpSections[0].image && helpSections[0].image.filename"
              :src="helpSections[0].image.filename"
              :alt="helpSections[0].image.alt || helpSections[0].headline"
              max-width="100%"
              class="mb-4 clickable-image"
              @click="openImageDialog(helpSections[0].image)"
            />
            
            <div v-html="renderRichText(helpSections[0].content)" class="mb-4"></div>

            <!-- Feedback for main section -->
            <div class="mt-2">
              <a href="#" @click.prevent="toggleFeedback(helpSections[0].slug)" :alvqa="'context-help-feedback-toggle-' + helpSections[0].slug">{{$t('lang_GENERIC_CAPTION_FEEDBACK_FORM_TOGGLE')}}</a>
            </div>
            <v-expand-transition>
              <div v-if="feedbackBySlug[helpSections[0].slug] && feedbackBySlug[helpSections[0].slug].open" class="pt-2">
                <v-form ref="feedbackFormMain">
                  <v-row no-gutters>
                    <v-col cols="12" sm="6" class="pr-2 mt-3 mb-3">
                      <v-text-field
                        v-model="feedbackBySlug[helpSections[0].slug].email"
                        :label="$t('lang_GENERIC_CAPTION_EMAIL')"
                        :rules="[rules.required, rules.email]"
                        :disabled="isSendingFeedback"
                        clearable
                        type="email"
                        dense
                        :alvqa="'context-help-feedback-email-' + helpSections[0].slug"
                      />
                    </v-col>
                  </v-row>
                  <v-row no-gutters class="mt-4">
                    <v-col cols="12" sm="9" class="pr-2 mb-2">
                      <v-textarea
                        v-model="feedbackBySlug[helpSections[0].slug].message"
                        :label="$t('lang_GENERIC_CAPTION_FEEDBACK')"
                        :rules="[rules.required]"
                        :disabled="isSendingFeedback"
                        auto-grow
                        clearable
                        rows="3"
                        dense
                        :alvqa="'context-help-feedback-message-' + helpSections[0].slug"
                      />
                    </v-col>
                  </v-row>
                  <v-row no-gutters class="mt-2">
                    <v-col cols="12" class="mt-2">
                      <BaseButton 
                        :label="'lang_GENERIC_CAPTION_SEND'" 
                        :alvqa="'context-help-feedback-send-' + helpSections[0].slug"
                        :loading="isSendingFeedback"
                        @click="sendFeedback(helpSections[0].slug)"
                      />
                    </v-col>
                  </v-row>
                </v-form>
                <div style="height:20px;"></div>
              </div>
            </v-expand-transition>
          </div>
          
          <!-- Sub-sections as collapsible panels -->
          <v-expansion-panels 
            v-if="helpSections.length > 1"
            multiple
            flat
            tile
            class="mt-4"
          >
            <v-expansion-panel
              v-for="(section, index) in helpSections.slice(1)"
              :key="index"
            >
              <v-expansion-panel-header class="pa-3">
                <strong>{{ section.headline }}</strong>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pa-3">
                <!-- Section image -->
                <v-img 
                  v-if="section.image && section.image.filename"
                  :src="section.image.filename"
                  :alt="section.image.alt || section.headline"
                  max-width="50%"
                  class="mb-3 clickable-image"
                  @click="openImageDialog(section.image)"
                />
                
                <div v-html="renderRichText(section.content)"></div>

                <!-- Feedback for subsection -->
                <div class="mt-2">
                  <a href="#" @click.prevent="toggleFeedback(section.slug)" :alvqa="'context-help-feedback-toggle-' + section.slug">{{$t('lang_GENERIC_CAPTION_FEEDBACK_FORM_TOGGLE')}}</a>
                </div>
                <v-expand-transition>
                  <div v-if="feedbackBySlug[section.slug] && feedbackBySlug[section.slug].open" class="pt-2">
                    <v-form :ref="'feedbackForm-' + section.slug">
                      <v-row no-gutters>
                        <v-col cols="12" sm="6" class="pr-2 mt-3 mb-3">
                          <v-text-field
                            v-model="feedbackBySlug[section.slug].email"
                            :label="$t('lang_GENERIC_CAPTION_EMAIL')"
                            :rules="[rules.required, rules.email]"
                            :disabled="isSendingFeedback"
                            clearable
                            type="email"
                            dense
                            :alvqa="'context-help-feedback-email-' + section.slug"
                          />
                        </v-col>
                      </v-row>
                      <v-row no-gutters class="mt-4">
                        <v-col cols="12" sm="9" class="pr-2 mb-2">
                          <v-textarea
                            v-model="feedbackBySlug[section.slug].message"
                            :label="$t('lang_GENERIC_CAPTION_FEEDBACK')"
                            :rules="[rules.required]"
                            :disabled="isSendingFeedback"
                            auto-grow
                            clearable
                            rows="3"
                            dense
                            :alvqa="'context-help-feedback-message-' + section.slug"
                          />
                        </v-col>
                      </v-row>
                      <v-row no-gutters class="mt-2">
                        <v-col cols="12" class="mt-2">
                          <BaseButton 
                            :label="'lang_GENERIC_CAPTION_SEND'" 
                            :alvqa="'context-help-feedback-send-' + section.slug"
                            :loading="isSendingFeedback"
                            @click="sendFeedback(section.slug)"
                          />
                        </v-col>
                      </v-row>
                    </v-form>
                    <div style="height:20px;"></div>
                  </div>
                </v-expand-transition>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>

        <!-- Empty state when no help content is available -->
        <v-card-text v-else class="pa-6">
          <div class="mb-5">
            {{ $t('lang_GENERIC_CAPTION_CONTEXT_HELP_NOT_TRANSLATED') }}
          </div>
          <!-- Inline feedback form replacing the Help Center button -->
          <div v-if="feedbackBySlug[emptySlug]">
            <v-form :ref="'feedbackForm-' + emptySlug" style="max-width:720px; width:100%; text-align:left;">
              <v-row no-gutters>
                <v-col cols="12" sm="6" class="pr-2 mt-3 mb-3">
                  <v-text-field
                    v-model="feedbackBySlug[emptySlug].email"
                    :label="$t('lang_GENERIC_CAPTION_EMAIL')"
                    :rules="[rules.required, rules.email]"
                    :disabled="isSendingFeedback"
                    clearable
                    type="email"
                    dense
                    :alvqa="'context-help-feedback-email-' + emptySlug"
                  />
                </v-col>
              </v-row>
              <v-row no-gutters class="mt-4">
                <v-col cols="12" sm="9" class="pr-2 mb-2">
                  <v-textarea
                    v-model="feedbackBySlug[emptySlug].message"
                    :label="$t('lang_GENERIC_CAPTION_FEEDBACK')"
                    :rules="[rules.required]"
                    :disabled="isSendingFeedback"
                    auto-grow
                    clearable
                    rows="3"
                    dense
                    :alvqa="'context-help-feedback-message-' + emptySlug"
                  />
                </v-col>
              </v-row>
              <v-row no-gutters class="mt-2">
                <v-col cols="12" class="mt-2">
                  <BaseButton 
                    :label="'lang_GENERIC_CAPTION_SEND'" 
                    :alvqa="'context-help-feedback-send-' + emptySlug"
                    :loading="isSendingFeedback"
                    :disabled="hasSentFeedback"
                    @click="sendFeedback(emptySlug)"
                  />
                </v-col>
              </v-row>
            </v-form>
          </div>
          <div style="height:20px;"></div>
        </v-card-text>

        <!-- Per-section feedback implemented above -->
      </v-card>
    </v-dialog>

    <!-- Image enlargement dialog -->
    <v-dialog 
      v-model="showImageDialog" 
      max-width="90vw"
      content-class="image-dialog"

    >
      <v-card>
        <v-card-actions class="justify-end pa-2">
          <BaseIcon
            icon="close"
            @click="showImageDialog = false"
            color="grey darken-2"
            :alvqa="'image-dialog-close'"
          />
        </v-card-actions>
        <v-img 
          v-if="selectedImage && selectedImage.filename"
          :src="selectedImage.filename"
          :alt="selectedImage.alt"
          contain
          max-height="80vh"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { richTextResolver, BlockTypes, MarkTypes } from '@storyblok/richtext'
import api from '@/api_alv'
import BaseIcon from '@/components/_buttons/BaseIcon.vue'
import BaseButton from '@/components/_buttons/BaseButton'

// Debug flag for Context Help - set to true to see detailed console logs for Storyblok chain issues
const DEBUG_CONTEXT_HELP = false

export default {
  name: 'ContextHelpDialog',
  components: {
    BaseIcon,
    BaseButton
  },
  data() {
    return {
      isLoading: false,
      helpSections: [],
      showImageDialog: false,
      selectedImage: null,
      navigationHistory: [], // Stack for back navigation
      currentContent: null, // Current content being displayed
      isConceptMode: false, // Whether we're showing a concept story
      feedbackBySlug: {},
      isSendingFeedback: false,
      hasSentFeedback: false,
      rules: {
        required: (v) => {
          const val = (v === 0) ? '0' : (v || '').toString().trim()
          return !!val || this.$t('lang_GENERAL_VALIDATION_MESSAGE_FIELD_REQUIRED')
        },
        email: (v) => {
          const val = (v || '').toString().trim()
          const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          return re.test(val) || this.$t('lang_GENERIC_MESSAGE_ERROR_EMAIL_INVALID')
        }
      }
    }
  },
  computed: {
    showDialog() {
      return this.$store.getters.getContextHelpDialog.show
    },
    dialogContext() {
      return this.$store.getters.getContextHelpDialog.context
    },
    dialogSection() {
      return this.$store.getters.getContextHelpDialog.section
    },
    dialogPanelTitle() {
      return this.$store.getters.getContextHelpDialog.panelTitle
    },
    externalHelpUrl() {
      // Fallback to external help center
      return 'https://alvdigital.atlassian.net/servicedesk/customer/portal/1'
    },
    emptySlug() {
      const base = this.dialogContext || 'help'
      return base + '-not-available'
    },
    showBackButton() {
      return this.isConceptMode && this.navigationHistory.length > 0
    },
    breadcrumbPath() {
      // If in concept mode, show only the concept title
      if (this.isConceptMode && this.currentContent && this.currentContent.headline) {
        return this.currentContent.headline
      }
      
      // Otherwise show normal breadcrumbs
      const breadcrumbs = []
      
      // Add parent (e.g., "Verlage")
      if (this.$route.meta.parentName) {
        breadcrumbs.push(this.$t(this.$route.meta.parentName))
      }
      
      // Add child (e.g., "Werke") 
      if (this.$route.meta.childName) {
        breadcrumbs.push(this.$t(this.$route.meta.childName))
      }
      
      // Add panel title if provided
      if (this.dialogPanelTitle) {
        breadcrumbs.push(this.dialogPanelTitle)
      }
      
      return breadcrumbs.join(' > ') || 'Help'
    }
  },
  watch: {
    showDialog(newVal) {
      if (newVal && this.dialogContext) {
        this.hasSentFeedback = false
        this.fetchHelpContent()
      } else if (!newVal) {
        // Close image dialog when help dialog closes
        this.showImageDialog = false
        this.hasSentFeedback = false
      }
    }
  },
  mounted() {
    // Add event delegation for concept links and clickable images
    this.handleConceptLinkClick = (event) => {
      if (event.target.classList.contains('concept-link')) {
        const storyUuid = event.target.getAttribute('data-story-uuid')
        if (storyUuid) {
          this.openConceptStory(storyUuid)
        }
      }
      
      // Handle clickable images
      if (event.target.tagName === 'IMG' && event.target.classList.contains('clickable-image')) {
        const imageData = {
          filename: event.target.src,
          alt: event.target.alt || 'Image'
        }
        this.openImageDialog(imageData)
      }
    }
    document.addEventListener('click', this.handleConceptLinkClick)
  },
  beforeDestroy() {
    // Clean up event listener
    if (this.handleConceptLinkClick) {
      document.removeEventListener('click', this.handleConceptLinkClick)
    }
  },
  methods: {
    toggleFeedback(slug) {
      if (!this.feedbackBySlug[slug]) {
        this.$set(this.feedbackBySlug, slug, { open: true, email: '', message: '' })
        return
      }
        const isOpening = !this.feedbackBySlug[slug].open
        this.feedbackBySlug[slug].open = isOpening
        // Clear validation when closing the form to avoid lingering red messages
        if (!isOpening) {
          let formRef = this.$refs['feedbackForm-' + slug]
          if (Array.isArray(formRef)) formRef = formRef[0]
          if (formRef && typeof formRef.resetValidation === 'function') {
            formRef.resetValidation()
          }
        }
    },
    async sendFeedback(slug) {
      let formRef = this.$refs['feedbackForm-' + slug] || this.$refs.feedbackFormMain
      if (!formRef) return
      // When refs are created inside v-for, Vue returns an array of component refs
      if (Array.isArray(formRef)) formRef = formRef[0]
      if (!formRef || typeof formRef.validate !== 'function') return
      const valid = formRef.validate()
      if (!valid) return
      try {
        this.isSendingFeedback = true
        // Keep help_context clean (no hash) for email placeholder; slug is sent separately
        const context = (this.dialogContext || 'unknown')
        const language = this.$i18n.locale || 'de'
        const languageCapitalized = language.toUpperCase()
        const now = new Date()
        const timestampIso = now.toISOString()
        const timestampLocal = now.toLocaleString()
        // Lookup section meta for better diagnostics
        const sectionMeta = (this.helpSections || []).find(s => s.slug === slug) || null
        // User/account info from stored userData (do not take language from here)
        let userName = null, loginName = null, accountName = null, customerNumber = null
        try {
          const ud = JSON.parse(localStorage.getItem('userData'))
          const u = ud && ud.user ? ud.user : null
          if (u) {
            userName = u.name || null
            loginName = u.username || null
            accountName = u.customerCompany || null
            customerNumber = u.customerNumber || null
          }
        } catch (e) { /* ignore */ }
        const payload = {
          user_email: (this.feedbackBySlug[slug] && this.feedbackBySlug[slug].email) || '',
          user_message: (this.feedbackBySlug[slug] && this.feedbackBySlug[slug].message) || '',
          help_context: context,
          language,
          language_capitalized: languageCapitalized,
          timestamp_iso: timestampIso,
          timestamp_local: timestampLocal,
          app: 'ALV',
          article_slug: slug || null,
          article_name: sectionMeta ? sectionMeta.name : null,
          article_headline: sectionMeta ? sectionMeta.headline : null,
          user_name: userName,
          login_name: loginName,
          account_name: accountName,
          customer_number: customerNumber
        }
        await api.triggerAction('email', payload, { action: 'sendHelpFeedback' })
        // Disable only the empty-state form after sending
        this.hasSentFeedback = (slug === this.emptySlug)
        this.$store.commit('setSnackbar', { type: 'success', msg: this.$t('lang_GENERIC_CAPTION_SENT') })
        if (this.feedbackBySlug[slug]) {
          this.feedbackBySlug[slug].email = ''
          this.feedbackBySlug[slug].message = ''
          // Keep empty-state form visible; only close toggled forms
          if (slug !== this.emptySlug) this.feedbackBySlug[slug].open = false
        }
        // Clear validation/error state after successful send so no red messages linger
        if (formRef) {
          if (typeof formRef.reset === 'function') formRef.reset()
          if (typeof formRef.resetValidation === 'function') formRef.resetValidation()
        }
      } catch (e) {
        this.$store.commit('setSnackbar', { type: 'error', msg: this.$t('lang_GENERIC_SNACKBAR_ERROR') })
      } finally {
        this.isSendingFeedback = false
      }
    },
    async fetchHelpContent() {
      this.isLoading = true
      this.helpSections = []
      
      try {
        const currentLanguage = this.$i18n.locale || 'de'
        
        const response = await api.fetchContextHelp(
          this.dialogContext,
          this.dialogSection,
          currentLanguage
        )
        
        if (response && response.length > 0) {
          // Chain the entries together using NextHelpSlug
          this.helpSections = this.assembleHelpChain(response, currentLanguage)
        } else {
          // Initialize empty-state feedback storage
          if (!this.feedbackBySlug[this.emptySlug]) {
            this.$set(this.feedbackBySlug, this.emptySlug, { open: true, email: '', message: '' })
          } else {
            this.feedbackBySlug[this.emptySlug].open = true
          }
        }
      } catch (error) {
        if (DEBUG_CONTEXT_HELP) {
          console.error('Error fetching context help:', error)
        }
        // Error fetching context help - silent fail
      } finally {
        this.isLoading = false
      }
    },
    
    assembleHelpChain(allEntries, currentLanguage) {
      // Create lookup map by story slug
      const entriesMap = {}
      allEntries.forEach(entry => {
        entriesMap[entry.slug] = entry
      })
      
      // Find the main entry (one without any NextHelpSlug pointing to it)
      const referencedEntries = new Set()
      allEntries.forEach(entry => {
        if (entry.content.NextHelpSlug) {
          referencedEntries.add(entry.content.NextHelpSlug)
        }
      })
      
      // Collect problems for debug output
      const problems = []
      
      // Check for broken links
      allEntries.forEach(entry => {
        const nextSlug = entry.content.NextHelpSlug
        if (nextSlug && !entriesMap[nextSlug]) {
          problems.push({
            type: 'BROKEN_LINK',
            article: entry.slug,
            target: nextSlug
          })
        }
      })
      
      const mainEntry = allEntries.find(entry => !referencedEntries.has(entry.slug))
      if (!mainEntry) {
        return allEntries[0] // Fallback to first entry if no clear main entry
      }
      
      // Follow the chain and return sections array
      const sections = []
      const visitedSlugs = new Set()
      let current = mainEntry
      let previousSlug = null
      let circularRef = null
      
      while (current) {
        // Check for circular reference
        if (visitedSlugs.has(current.slug)) {
          circularRef = {
            article: previousSlug,
            target: current.slug
          }
          break
        }
        visitedSlugs.add(current.slug)
        previousSlug = current.slug
        
        // Check if this entry references another story for content
        let contentSource = current.content
        if (current.content.ContentReference && current.content.ContentReference.content) {
          contentSource = current.content.ContentReference.content
        }
        
        sections.push({
          slug: current.slug,
          name: current.name,
          headline: contentSource.Headline,
          content: contentSource.Paragraph,
          image: contentSource.Image || null
        })
        current = entriesMap[current.content.NextHelpSlug]
      }
      
      // Check for orphaned articles
      const chainedSlugs = new Set(sections.map(s => s.slug))
      const orphaned = allEntries.filter(e => !chainedSlugs.has(e.slug))
      
      // Output debug only if there are problems
      if (DEBUG_CONTEXT_HELP && (problems.length > 0 || circularRef || orphaned.length > 0)) {
        console.log(`\n⚠️ STORYBLOK HELP PROBLEMS [${this.dialogContext}] (${currentLanguage})`)
        
        if (circularRef) {
          console.log(`\n❌ CIRCULAR REFERENCE:`)
          console.log(`   Article: "${circularRef.article}"`)
          console.log(`   Problem: Its NextHelpSlug points to "${circularRef.target}" which is already in the chain`)
          console.log(``)
          console.log(`   👉 QUESTION: Should "${circularRef.article}" be the LAST article in the chain?`)
          if (orphaned.length > 0) {
            console.log(`      - If YES: Set NextHelpSlug to empty/blank`)
            console.log(`      - If NO: Set NextHelpSlug to one of the ORPHANED articles below`)
          } else {
            console.log(`      - If YES: Set NextHelpSlug to empty/blank`)
            console.log(`      - If NO: Which article should come after it?`)
          }
        }
        
        if (problems.length > 0) {
          console.log(`\n❌ BROKEN LINKS:`)
          problems.forEach(p => {
            console.log(`   Article: "${p.article}"`)
            console.log(`   Problem: NextHelpSlug "${p.target}" does not exist`)
            console.log(`   👉 FIX: Correct the slug or remove it if this should be the last article`)
          })
        }
        
        if (orphaned.length > 0) {
          console.log(`\n⚠️ ORPHANED (${orphaned.length} articles loaded but NOT in chain):`)
          orphaned.forEach(e => {
            const headline = e.content.Headline || '(no headline)'
            const pointsTo = e.content.NextHelpSlug || '(end)'
            console.log(`   "${e.slug}"`)
            console.log(`      Headline: "${headline}"`)
            console.log(`      NextHelpSlug: "${pointsTo}"`)
          })
          console.log(``)
          console.log(`   👉 QUESTION: Where should these articles appear in the chain?`)
          console.log(`      Find the article that should come BEFORE them and set its NextHelpSlug accordingly.`)
        }
        
        console.log('')
      }
      
      return sections
    },
    
    renderRichText(richTextObject) {
      if (!richTextObject) return ''
      
      // Reuse the same rich text renderer configuration from Dashboard
      const { render } = richTextResolver({
        resolvers: {
          'emoji': (node) => {
            const emoji = node.attrs && node.attrs.emoji ? node.attrs.emoji : ''
            return emoji
          },
          [BlockTypes.IMAGE]: (node) => {
            // Make images clickable with proper class
            const src = node.attrs.src || ''
            const alt = node.attrs.alt || 'Image'
            const title = node.attrs.title || ''
            return `<img src="${src}" alt="${alt}" title="${title}" class="clickable-image" loading="lazy" />`
          },
          [BlockTypes.TABLE]: (node) => {
            const children = Array.isArray(node.children) ? node.children.join('') : node.children
            return `<table class="help-table">${children}</table>`
          },
          [BlockTypes.TABLE_ROW]: (node) => {
            const children = Array.isArray(node.children) ? node.children.join('') : node.children
            return `<tr>${children}</tr>`
          },
          [BlockTypes.TABLE_CELL]: (node) => {
            const children = Array.isArray(node.children) ? node.children.join('') : node.children
            return `<td>${children}</td>`
          },
          [BlockTypes.TABLE_HEADER]: (node) => {
            const children = Array.isArray(node.children) ? node.children.join('') : node.children
            return `<th>${children}</th>`
          },
          [MarkTypes.LINK]: (node) => {
            if (node.attrs.linktype === 'story') {
              // Story link - create clickable concept link
              const storyUuid = node.attrs.uuid
              
              // Try different ways to extract link text
              let linkText = 'Link'
              if (node.text) {
                // Text directly on node (most common)
                linkText = node.text
              } else if (node.content && node.content.length > 0) {
                if (node.content[0].text) {
                  linkText = node.content[0].text
                } else if (node.content[0].content && node.content[0].content[0] && node.content[0].content[0].text) {
                  linkText = node.content[0].content[0].text
                }
              }
              
              return `<span class="concept-link" data-story-uuid="${storyUuid}">${linkText}</span>`
            } else {
              // Regular external link
              const href = node.attrs.href || '#'
              let linkText = 'Link'
              if (node.content && node.content.length > 0 && node.content[0].text) {
                linkText = node.content[0].text
              }
              return `<a href="${href}" target="_blank" rel="noopener noreferrer">${linkText}</a>`
            }
          }
        }
      })
      
      try {
        return render(richTextObject)
      } catch (error) {
        // Error rendering help content - return empty string
        return ''
      }
    },
    
    openImageDialog(image) {
      this.selectedImage = image
      this.showImageDialog = true
    },
    
    closeDialog() {
      // Reset navigation state when closing
      this.resetNavigationState()
      
      this.$store.commit('setContextHelpDialog', {
        show: false,
        context: null,
        section: null
      })
    },
    
    resetNavigationState() {
      this.navigationHistory = []
      this.currentContent = null
      this.isConceptMode = false
    },
    
    async goBack() {
      if (this.navigationHistory.length > 0) {
        const previousContext = this.navigationHistory.pop()
        
        // If we have more history, stay in concept mode
        if (this.navigationHistory.length > 0) {
          // Load previous concept
          await this.loadConceptByContext(previousContext)
        } else {
          // Return to original help content
          this.isConceptMode = false
          this.currentContent = null
          await this.fetchHelpContent()
        }
      }
    },
    
    async openConceptStory(storyUuid) {
      try {
        this.isLoading = true
        
        // Save current state to navigation history
        if (!this.isConceptMode) {
          // First time entering concept mode - save original context
          this.navigationHistory.push({
            context: this.dialogContext,
            title: this.breadcrumbPath,
            isOriginal: true
          })
        } else if (this.currentContent) {
          // Save current concept to history
          this.navigationHistory.push({
            storyUuid: this.currentContent.uuid,
            title: this.currentContent.headline,
            isOriginal: false
          })
        }
        
        // Fetch concept story content
        await this.loadConceptByUuid(storyUuid)
        
      } catch (error) {
        // Error loading concept story - silent fail
      } finally {
        this.isLoading = false
      }
    },
    
    async loadConceptByUuid(storyUuid) {
      try {
        const currentLanguage = this.$i18n.locale || 'de'
        const story = await api.fetchStoryByUuid(storyUuid, currentLanguage)
        
        if (story) {
          this.isConceptMode = true
          this.currentContent = {
            uuid: storyUuid,
            headline: story.content.Headline || 'Concept',
            content: story.content.Paragraph || null,
            image: story.content.Image || null
          }
          
          this.helpSections = [{
            headline: this.currentContent.headline,
            content: this.currentContent.content,
            image: this.currentContent.image
          }]
        } else {
          // Fallback if story not found
          this.isConceptMode = true
          this.currentContent = {
            uuid: storyUuid,
            headline: 'Concept Not Found',
            content: { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'The requested concept could not be loaded.' }] }] }
          }
          this.helpSections = [{
            headline: this.currentContent.headline,
            content: this.currentContent.content,
            image: null
          }]
        }
      } catch (error) {
        // Fallback on error
        this.isConceptMode = true
        this.currentContent = {
          uuid: storyUuid,
          headline: 'Error Loading Concept',
          content: { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'There was an error loading this concept.' }] }] }
        }
        this.helpSections = [{
          headline: this.currentContent.headline,
          content: this.currentContent.content,
          image: null
        }]
      }
    },
    
    async loadConceptByContext(contextData) {
      if (contextData.isOriginal) {
        // Return to original help content
        this.isConceptMode = false
        this.currentContent = null
        await this.fetchHelpContent()
      } else {
        // Load previous concept
        await this.loadConceptByUuid(contextData.storyUuid)
      }
    }
  }
}
</script>

<style scoped>
/* Use ::v-deep to style v-html injected content */
::v-deep .help-table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  font-size: 14px;
}

::v-deep .help-table th,
::v-deep .help-table td {
  border: 1px solid #ccc;
  padding: 10px 12px;
  text-align: left;
  vertical-align: top;
}

::v-deep .help-table th {
  background-color: #f0f0f0;
  font-weight: 600;
  white-space: nowrap;
}

::v-deep .help-table td {
  background-color: #ffffff;
}

::v-deep .help-table tr:nth-child(even) td {
  background-color: #fafafa;
}

/* Consistent spacing for help content */
.v-card__text h3 {
  margin-bottom: 16px !important;
  margin-top: 0 !important;
}

/* Help dialog expansion panel styling - restore original flat appearance */
.v-expansion-panel {
  background-color: white !important;
  margin-bottom: 8px !important;
}

.v-expansion-panel-header {
  background-color: #f5f5f5 !important;
  min-height: 48px !important;
  max-height: 48px !important;
}

/* Force consistent header height when active/expanded */
.v-expansion-panel--active .v-expansion-panel-header {
  min-height: 48px !important;
  max-height: 48px !important;
}

.v-expansion-panel-content {
  background-color: white !important;
}

.v-expansion-panel-content__wrap {
  padding: 12px 16px 16px 16px !important;
}

/* General clickable image styling (for existing images) */
.clickable-image {
  cursor: pointer !important;
  transition: transform 0.2s ease, opacity 0.2s ease !important;
}

.clickable-image:hover {
  opacity: 0.9 !important;
  transform: scale(1.01) !important;
}

/* Image dialog styling */
.image-dialog .v-card {
  background: white !important;
  box-shadow: none !important;
  border-radius: 8px !important;
  padding: 20px !important;
}

.image-dialog .v-card__actions {
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  z-index: 1 !important;
}

/* Concept link styling */
.concept-link {
  color: #1976d2 !important;
  cursor: pointer !important;
  text-decoration: underline !important;
  font-weight: 500 !important;
  transition: color 0.2s ease !important;
}

.concept-link:hover {
  color: #1565c0 !important;
  text-decoration: none !important;
}

/* Storyblok embedded images styling */
.v-card__text img {
  max-width: 100% !important;
  height: auto !important;
  display: block !important;
  margin: 16px auto !important;
  border-radius: 4px !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
}

/* Make clickable images clearly interactive */
.v-card__text img.clickable-image {
  cursor: pointer !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease !important;
}

.v-card__text img.clickable-image:hover {
  transform: scale(1.02) !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2) !important;
}

/* Constrain large images to reasonable size */
.v-card__text img[width] {
  max-width: 600px !important;
  width: auto !important;
}

/* For very wide diagrams, scale them down */
.v-card__text img[src*="diagram"],
.v-card__text img[src*="chart"] {
  max-width: 500px !important;
  width: auto !important;
}
</style> 